import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Tag, CheckCircle, XCircle } from 'lucide-react';
import SubscriptionRegistrationModal from '../components/SubscriptionRegistrationModal';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const SubscriptionSimple = () => {
  const [subscriptionPlans, setSubscriptionPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const { isAuthenticated, token } = useAuth();
  const { toast } = useToast();

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoResult, setPromoResult] = useState(null); // { valid, message, discount_amount, final_amount, coupon_id }
  const [appliedPromo, setAppliedPromo] = useState(null);

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const plansResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/plans`);
      
      if (!plansResponse.ok) {
        throw new Error(`API error: ${plansResponse.status}`);
      }
      
      const plans = await plansResponse.json();
      
      // Validate plans data
      if (!plans || Object.keys(plans).length === 0) {
        throw new Error('No subscription plans received');
      }
      
      setSubscriptionPlans(plans);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
      toast({
        variant: "destructive",
        title: "Error Loading Plans",
        description: "Could not load subscription plans. Please refresh the page."
      });
      // Set default plans as fallback
      setSubscriptionPlans({
        seekers: {
          name: "🌟 The Seekers Path",
          description: "Complete access to reclaim your ancestral legacy",
          monthly_price: 9.99,
          yearly_price: 119.88,
          activation_fee: 0,
          features: ["Full access to all features"]
        },
        crown: {
          name: "👑 The Crown Subscription", 
          description: "Ultimate sovereignty with official tribal documentation",
          monthly_price: 9.99,
          yearly_price: 119.88,
          activation_fee: 129.99,
          features: ["Everything from Seekers Path", "Official Documentation"]
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const validatePromoCode = async (tier) => {
    if (!promoCode.trim()) {
      toast({
        variant: "destructive",
        title: "Enter Promo Code",
        description: "Please enter a promo code to apply"
      });
      return;
    }

    setPromoValidating(true);
    setPromoResult(null);

    try {
      const plan = subscriptionPlans[tier];
      const amount = plan?.monthly_price || 0;

      // Use public endpoint for non-authenticated users, authenticated endpoint for logged-in users
      const endpoint = isAuthenticated 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/coupons/validate`
        : `${process.env.REACT_APP_BACKEND_URL}/api/coupons/validate-public`;
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (isAuthenticated) {
        headers['Authorization'] = `Bearer ${token || localStorage.getItem('auth_token')}`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          code: promoCode.trim().toUpperCase(),
          subscription_tier: tier,
          amount: amount
        })
      });

      const result = await response.json();
      setPromoResult(result);

      if (result.valid) {
        setAppliedPromo({
          code: promoCode.trim().toUpperCase(),
          tier: tier,
          ...result
        });
        toast({
          title: "Promo Code Applied!",
          description: result.message
        });
      } else {
        toast({
          variant: "destructive",
          title: "Invalid Code",
          description: result.message
        });
      }
    } catch (error) {
      console.error('Promo validation error:', error);
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Failed to validate promo code. Please try again."
      });
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromoCode = () => {
    setPromoCode('');
    setPromoResult(null);
    setAppliedPromo(null);
  };

  const handleSubscription = async (tier) => {
    if (!isAuthenticated) {
      // Show registration modal for new users
      setSelectedTier(tier);
      setShowRegistrationModal(true);
      return;
    }

    // If Free tier, just show registration modal (even for logged in users)
    if (tier === 'free') {
      setSelectedTier(tier);
      setShowRegistrationModal(true);
      return;
    }

    // User is authenticated, proceed with checkout for paid tiers
    try {
      setProcessingPayment(tier);
      
      // Get current URL
      const currentUrl = window.location.origin;

      // Check if promo code is applied for this tier
      if (appliedPromo && appliedPromo.tier === tier && appliedPromo.valid) {
        // Use coupon checkout endpoint
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/coupons/apply-to-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({
            code: appliedPromo.code,
            subscription_tier: tier,
            billing_interval: 'monthly',
            origin_url: currentUrl
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to create checkout session');
        }

        const data = await response.json();
        
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        // Regular checkout without promo code
        const requestBody = {
          subscription_tier: tier,
          billing_interval: 'monthly',
          origin_url: currentUrl
        };

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to create checkout session');
        }

        const data = await response.json();
        
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      }

    } catch (error) {
      console.error('Subscription error:', error);
      setProcessingPayment(null);
      
      toast({
        variant: "destructive",
        title: "Subscription Failed",
        description: error.message || "Failed to start subscription process. Please try again.",
      });
    }
  };

  const getDisplayPrice = (tier, plan) => {
    if (appliedPromo && appliedPromo.tier === tier && appliedPromo.valid) {
      return appliedPromo.final_amount;
    }
    return plan.monthly_price;
  };

  const getOriginalPrice = (tier, plan) => {
    if (appliedPromo && appliedPromo.tier === tier && appliedPromo.valid) {
      return plan.monthly_price;
    }
    return null;
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-center mb-8">Choose Your Journey</h1>
        
        {/* Promo Code Section - Visible to all users */}
        <div className="max-w-md mx-auto mb-8">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center mb-3">
              <Tag className="w-5 h-5 text-green-600 mr-2" />
              <span className="font-semibold text-gray-700">Have a Promo Code?</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="flex-1"
                disabled={promoValidating}
                data-testid="promo-code-input"
              />
              {appliedPromo ? (
                <Button 
                  variant="outline" 
                  onClick={clearPromoCode}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  data-testid="clear-promo-btn"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Clear
                </Button>
                ) : (
                  <Button 
                    onClick={() => {
                      // Find the first paid tier to validate against
                      const paidTier = Object.keys(subscriptionPlans).find(t => t !== 'free');
                      if (paidTier) validatePromoCode(paidTier);
                    }}
                    disabled={promoValidating || !promoCode.trim()}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="apply-promo-btn"
                  >
                    {promoValidating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Apply'
                    )}
                  </Button>
                )}
              </div>
              
              {/* Promo Result Display */}
              {promoResult && (
                <div className={`mt-3 p-3 rounded-lg flex items-center ${
                  promoResult.valid 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {promoResult.valid ? (
                    <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                  )}
                  <span className="text-sm font-medium">{promoResult.message}</span>
                </div>
              )}
              
              {appliedPromo && appliedPromo.valid && (
                <div className="mt-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <div className="text-sm text-green-800">
                    <span className="font-semibold">Discount Applied:</span> {' '}
                    {appliedPromo.discount_type === 'percentage' 
                      ? `${appliedPromo.discount_value}% off`
                      : `$${appliedPromo.discount_value} off`
                    }
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    You save ${appliedPromo.discount_amount?.toFixed(2)} on your subscription!
                  </div>
                </div>
              )}
            </div>
          </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {Object.entries(subscriptionPlans).map(([tier, plan]) => {
            const displayPrice = getDisplayPrice(tier, plan);
            const originalPrice = getOriginalPrice(tier, plan);
            const hasDiscount = appliedPromo && appliedPromo.tier === tier && appliedPromo.valid;
            
            return (
              <div 
                key={tier} 
                className={`bg-white rounded-lg shadow-lg p-6 ${
                  tier === 'crown' ? 'transform scale-105 border-2 border-amber-400' : ''
                } ${hasDiscount ? 'ring-2 ring-green-400' : ''}`}
                data-testid={`plan-card-${tier}`}
              >
                <div className="text-center mb-4">
                  {tier === 'free' && <span className="text-2xl">🆓</span>}
                  {tier === 'seekers' && <span className="text-2xl">🌟</span>}
                  {tier === 'crown' && <span className="text-2xl">👑</span>}
                </div>
                
                <h2 className="text-2xl font-bold mb-2 text-center">{plan.name}</h2>
                <p className="text-gray-600 mb-4 text-center">{plan.description}</p>
                
                <div className="text-center mb-6">
                  {tier === 'crown' && plan.activation_fee > 0 && (
                    <div className="text-lg text-amber-600 font-medium mb-2">
                      🔍 ${plan.activation_fee} One-Time
                      <div className="text-xs text-gray-500">Genealogy Activation</div>
                    </div>
                  )}
                  
                  {/* Price Display with Discount */}
                  <div className="text-3xl font-bold">
                    {displayPrice === 0 || displayPrice === undefined ? (
                      'Free'
                    ) : (
                      <>
                        {originalPrice && (
                          <span className="text-lg text-gray-400 line-through mr-2">
                            ${originalPrice}
                          </span>
                        )}
                        <span className={hasDiscount ? 'text-green-600' : ''}>
                          ${displayPrice?.toFixed(2) || '0.00'}
                        </span>
                        <span className="text-lg text-gray-500">/month</span>
                      </>
                    )}
                  </div>
                  
                  {hasDiscount && (
                    <div className="mt-2 inline-block bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                      🎉 {appliedPromo.discount_type === 'percentage' 
                        ? `${appliedPromo.discount_value}% OFF` 
                        : `$${appliedPromo.discount_value} OFF`}
                    </div>
                  )}
                </div>
                
                <div className="space-y-2 mb-6">
                  {plan.features?.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-green-600 mr-2 mt-1">✓</span>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {plan.tagline && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm italic text-gray-700 text-center">&quot;{plan.tagline}&quot;</p>
                  </div>
                )}
                
                <Button 
                  className={`w-full ${
                    tier === 'free'
                      ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800'
                      : tier === 'seekers' 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700'
                      : 'bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800'
                  } text-white`}
                  onClick={() => handleSubscription(tier)}
                  disabled={processingPayment === tier}
                  data-testid={`subscribe-btn-${tier}`}
                >
                  {processingPayment === tier ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    tier === 'free' ? 'Get Started Free' : tier === 'seekers' ? 'Activate Seekers Path' : 'Activate Crown Plan'
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Subscription Registration Modal */}
        <SubscriptionRegistrationModal
          isOpen={showRegistrationModal}
          onClose={() => {
            setShowRegistrationModal(false);
            setSelectedTier(null);
          }}
          subscriptionTier={selectedTier}
          subscriptionPlan={selectedTier ? subscriptionPlans[selectedTier] : null}
          appliedPromoCode={appliedPromo && appliedPromo.tier === selectedTier ? appliedPromo : null}
          onSubscriptionSuccess={() => {
            setShowRegistrationModal(false);
            setSelectedTier(null);
          }}
        />
      </div>
    </div>
  );
};

export default SubscriptionSimple;
